import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { authApi } from '@/lib/pillarApi';
import { buildRequiredTotpSetupPath } from '@/lib/requiredTotpSetup';
import { resetReactQueryAfterRoleSwitch } from '@/lib/effectiveSessionRefresh';
import { sessionSensitiveAuthApi } from '@/lib/sessionSensitiveAuthApi';
import { runInBackground } from '@/lib/utils';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import {
    useAuthStore,
    type EffectiveSessionGeneration } from '@/store/auth';
import {
    ActiveRoleAssignmentResponse,
    SetActiveRoleAssignmentResponse,
    getAllRoleAssignments,
    RoleAssignmentDetailed,
    type RoleAssignmentsContainer,
} from '@anchorbase/pillar-sdk';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Status } from '@anchorbase/pillar-sdk';

interface OrganizationRoleSwitcherProps {
    user: RoleAssignmentsContainer;
}

function requireRoleAssignmentUuid(
    roleAssignment: RoleAssignmentDetailed
): RoleAssignmentDetailed & { role_assignment_uuid: string } {
    if (roleAssignment.role_assignment_uuid === undefined) {
        throw new Error('role_assignment_uuid is required for role assignment operations');
    }
    return {
        ...roleAssignment,
        role_assignment_uuid: roleAssignment.role_assignment_uuid,
    };
}

export function isSwitchableRoleAssignment(roleAssignment: RoleAssignmentDetailed): boolean {
    if (roleAssignment.organization_id === null) {
        return true;
    }
    return (
        roleAssignment.organization_status === undefined ||
        roleAssignment.organization_status === Status.ACTIVE
    );
}

export { resetReactQueryAfterRoleSwitch };

export function getPostRoleSwitchNavigationPath(
    response: SetActiveRoleAssignmentResponse,
    currentPathname: string
): string | null {
    if (response.requires_totp_setup) {
        return buildRequiredTotpSetupPath(response.totp_setup_activation_target);
    }
    if (currentPathname === '/login/mfa-setup') {
        return '/dashboard';
    }
    return null;
}

export function OrganizationRoleSwitcher({ user }: OrganizationRoleSwitcherProps) {
    const log = createComponentLogger('OrganizationRoleSwitcher');
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const activeRoleAssignmentId: number | null = useAuthStore(
        (state) => state.activeRoleAssignmentId
    );
    const setActiveRoleAssignmentId: (activeRoleAssignmentId: number | null) => void = useAuthStore(
        (state) => state.setActiveRoleAssignmentId
    );
    const setActiveRoleAssignmentIdForEffectiveSessionGeneration: (
        activeRoleAssignmentId: number | null,
        generation: EffectiveSessionGeneration
    ) => boolean = useAuthStore(
        (state) => state.setActiveRoleAssignmentIdForEffectiveSessionGeneration
    );
    const getEffectiveSessionGeneration: () => EffectiveSessionGeneration =
        useAuthStore((state) => state.getEffectiveSessionGeneration);
    const activeRoleAssignmentGenerationRef = useRef<EffectiveSessionGeneration>(
        getEffectiveSessionGeneration()
    );

    const setActiveRoleAssignmentMutation = useMutation({
        mutationFn: authApi.setActiveRoleAssignment,
        onSuccess: (data) => {
            // Avoid carrying stale pkey state while the active-role query refetches.
            setActiveRoleAssignmentId(null);
            resetReactQueryAfterRoleSwitch(queryClient, log);
            const nextPath = getPostRoleSwitchNavigationPath(data, location.pathname);
            if (nextPath !== null) {
                runInBackground(navigate(nextPath), (err) => {
                    log.error('Error navigating after role switch', err);
                });
            }
        },
    });

    const roleAssignments: RoleAssignmentDetailed[] =
        getAllRoleAssignments(user).filter(isSwitchableRoleAssignment);

    // Fetch active role assignment from current session
    const { data: activeRoleAssignmentData } = useQuery<ActiveRoleAssignmentResponse | null>({
        queryKey: ['activeRoleAssignment'],
        queryFn: async () => {
            const activeRoleAssignment =
                await sessionSensitiveAuthApi.getActiveRoleAssignment();
            activeRoleAssignmentGenerationRef.current =
                getEffectiveSessionGeneration();
            return activeRoleAssignment;
        },
        enabled: !!user,
        staleTime: 30000,
    });
    useEffect(() => {
        if (activeRoleAssignmentData === undefined) {
            return;
        }
        const nextActiveRoleAssignmentId: number | null =
            roleAssignments.find(
                (roleAssignment) =>
                    roleAssignment.role_assignment_uuid ===
                    activeRoleAssignmentData?.role_assignment_uuid
            )?.role_assignment_id ?? null;
        if (activeRoleAssignmentId === nextActiveRoleAssignmentId) {
            return;
        }
        setActiveRoleAssignmentIdForEffectiveSessionGeneration(
            nextActiveRoleAssignmentId,
            activeRoleAssignmentGenerationRef.current
        );
    }, [
        activeRoleAssignmentData,
        activeRoleAssignmentId,
        roleAssignments,
        setActiveRoleAssignmentIdForEffectiveSessionGeneration,
    ]);

    // Find the current active role assignment from the fetched data
    const activeRoleAssignment =
        activeRoleAssignmentData !== null && activeRoleAssignmentData !== undefined
            ? (roleAssignments.find(
                  (roleAssignment) =>
                      roleAssignment.role_assignment_uuid ===
                      activeRoleAssignmentData.role_assignment_uuid
              ) ?? null)
            : null;

    // If user has no roles (or only one), no switching UI is needed.
    if (roleAssignments.length <= 1) {
        return (
            <div className="text-xs text-gray-500 truncate">
                {roleAssignments[0]?.role ?? 'No roles assigned'}
            </div>
        );
    }

    const roleAssignmentOptions = roleAssignments.map((roleAssignment) =>
        requireRoleAssignmentUuid(roleAssignment)
    );

    const handleValueChange = (value: string) => {
        setActiveRoleAssignmentMutation.mutate(value);
    };

    const formatRoleAssignment = (roleAssignment: RoleAssignmentDetailed) => {
        if (roleAssignment.organization_id === null) {
            return `${roleAssignment.role} (internal)`;
        }
        const orgName =
            roleAssignment.organization_name || `Org #${roleAssignment.organization_id}`;
        return `${roleAssignment.role} @ ${orgName}`;
    };

    if (activeRoleAssignment && activeRoleAssignment.role_assignment_uuid === undefined) {
        throw new Error('role_assignment_uuid is required for role assignment operations');
    }
    const currentValue: string = activeRoleAssignment?.role_assignment_uuid ?? '';

    return (
        <Select
            value={currentValue}
            onValueChange={handleValueChange}
            disabled={setActiveRoleAssignmentMutation.isPending}
        >
            <SelectTrigger className="h-auto py-1 px-2 text-xs bg-gray-50 border-gray-200">
                <SelectValue placeholder="Select role">
                    {activeRoleAssignment ? (
                        <div className="text-left">
                            <div className="font-medium text-gray-900 truncate">
                                {activeRoleAssignment.organization_id === null
                                    ? 'Internal'
                                    : activeRoleAssignment.organization_name ||
                                      `Org #${activeRoleAssignment.organization_id}`}
                            </div>
                            <div className="text-gray-500 truncate">
                                {activeRoleAssignment.role}
                            </div>
                        </div>
                    ) : (
                        <span className="text-gray-500">No active role</span>
                    )}
                </SelectValue>
            </SelectTrigger>
            <SelectContent>
                {roleAssignmentOptions.map((roleAssignment) => (
                    <SelectItem
                        key={roleAssignment.role_assignment_id}
                        value={roleAssignment.role_assignment_uuid}
                    >
                        {formatRoleAssignment(roleAssignment)}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}
